import BigNumber from 'bignumber.js'
import { useMemo } from 'react'
import { useExchangeStore } from './exchangeStore'
import { useIndexPrice } from './indexprice'
import { useTradePosition } from './tradePosition'
import { useTradeStore } from './tradeStore'
import { useCurrency } from './useCurrency'

export function useAvaiblecheck() {
	const { data: tradeStore } = useTradeStore()
	const { data, global: balance } = useTradePosition()

	const { EPs } = useExchangeStore()
	const { krwrate, getRate } = useCurrency()
	const { getPrice, reset } = useIndexPrice(tradeStore?.symbol)

	// ✅ 총 PNL 계산을 useMemo로 최적화
	const totalpnl = useMemo(() => {
		if (!data || data.length === 0) return 0

		let total = 0

		data.forEach((item: any) => {
			const E = EPs?.find((i: any) => item.exchange_pairs_id == i.id)
			if (!E) return

			const currencyrate = getRate(E?.currency)
			const rate = BigNumber(krwrate?.price || 0)
				.div(currencyrate?.price || 0)
				.toNumber()

			const currentPrice = getPrice(item?.symbol)

			// 가격이 없으면 skip (reset 호출 제거)
			if (!currentPrice || currentPrice === 0) {
				return
			}

			let pnl = 0

			if (item?.long_short === 'L') {
				// Long 포지션: (현재가 - 진입가) * 수량 * 계약승수 * 환율
				pnl = BigNumber(currentPrice)
					.minus(item?.price)
					.multipliedBy(item?.amount)
					.multipliedBy(E?.contractamt)
					.multipliedBy(rate)
					.toNumber()
			} else {
				// Short 포지션: (진입가 - 현재가) * 수량 * 계약승수 * 환율
				pnl = BigNumber(item?.price)
					.minus(currentPrice)
					.multipliedBy(item?.amount)
					.multipliedBy(E?.contractamt)
					.multipliedBy(rate)
					.toNumber()
			}

			total = new BigNumber(total).plus(pnl).toNumber()
		})

		return total
	}, [data, EPs, krwrate, getRate, getPrice])

	// ✅ 가용 자산 계산을 useMemo로 최적화
	const available = useMemo(() => {
		return BigNumber(balance?.balance || 0)
			.minus(balance?.lockbalance || 0)
			.plus(totalpnl)
	}, [balance?.balance, balance?.lockbalance, totalpnl])

	// ✅ 현재 거래쌍 정보를 useMemo로 최적화
	const EP = useMemo(() => {
		return EPs?.find((i: any) => i.id == tradeStore?.ep_id)
	}, [EPs, tradeStore?.ep_id])

	// ✅ Long/Short 가용 수량 계산을 useMemo로 최적화
	const { availableLong, availableShort } = useMemo(() => {
		const margin = BigNumber(tradeStore?.minimum_margin || 0)

		// margin이 0이면 계산 불가
		if (margin.isZero()) {
			return { availableLong: 0, availableShort: 0 }
		}

		// 기본 가용 수량 계산
		const baseAvailable = Math.floor(available.dividedBy(margin).toNumber())
		const maxAmount = EP?.max_amount || 0

		let long = Math.min(baseAvailable, maxAmount)
		let short = Math.min(baseAvailable, maxAmount)

		// 현재 거래쌍의 포지션 필터링
		const poslist = data?.filter(
			(i: any) => i?.exchange_pairs_id == tradeStore?.ep_id
		)

		// 포지션에 따른 가용 수량 조정
		poslist?.forEach((item: any) => {
			if (item?.long_short == 'L') {
				// Long 포지션 보유시: Short 가능 수량 증가
				short = short + item?.amount
				// Long 가능 수량은 10계약 이상일 때만 감소
				if (long >= 10) {
					long = long - item?.amount
				}
			} else {
				// Short 포지션 보유시: Long 가능 수량 증가
				long = long + item?.amount
				// Short 가능 수량은 10계약 이상일 때만 감소
				if (short >= 10) {
					short = short - item?.amount
				}
			}
		})

		// 음수 방지
		return {
			availableLong: Math.max(0, long),
			availableShort: Math.max(0, short)
		}
	}, [available, tradeStore?.minimum_margin, tradeStore?.ep_id, EP?.max_amount, data])

	return { availableLong, availableShort }
}
